'use client';

// Side effect: patches insertBefore/removeChild against browser page translation (FUN-5044).
import { c as _c } from "react/compiler-runtime";
import '~/utils/translation-dom-guard';
import { QueryClientProvider } from '@tanstack/react-query';
import { createReactQueryClient } from '~/modules/react-query/client';
import React, { useEffect, useMemo } from 'react';
import type { I18N_CONF } from '@funkey/locales/config';
import { PostHogProvider } from 'posthog-js/react';
import dynamic from 'next/dynamic';
import { getI18n } from '@funkey/locales';
import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';
import { CartCtxProvider } from '~/features/cart/context';
import { GraphQLClientProvider } from '~/modules/api';
import { NotificationsContext } from '~/features/notifications/useNotifications';
import NotificationsPortal from '~/features/notifications/NotificationsPortal';
import { NotificationDispatcher } from '~/features/notifications/NotificationDispatcher';
import { Visitor } from '~/features/visitor';
import { _POSTHOG_SHARED_CONFIG } from '~/modules/posthog/shared';
import { updatePostHogDebugState } from '~/modules/posthog/PostHogDebugState';
import { PostHog_ClientFeaturesSDK } from '~/modules/posthog/features/client';
import { useCookie } from '~/utils/react';
import { I18nContext_Provider } from '~/features/i18n/react';
import { TRPCProvider } from '~/modules/trpc/client';
import env from '~/env';
import { Auth } from '~/modules/auth/client';
const GlobalContexts: React.FC<{
  locale: I18N_CONF.Locale;
  children: React.ReactNode;
}> = props => {
  const $ = _c(18);
  let t0;
  if ($[0] !== props.locale) {
    t0 = getI18n(props.locale);
    $[0] = props.locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const i18n = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      notifications: new NotificationDispatcher(),
      queryClient: createReactQueryClient()
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const deps = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      "x-portal": "WEB"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.children) {
    t3 = <CartCtxProvider>{props.children}</CartCtxProvider>;
    $[4] = props.children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Maybe_ReactQueryDevtools />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3) {
    t5 = <GraphQLClientProvider headers={t2}>{t3}{t4}</GraphQLClientProvider>;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== i18n.locale || $[10] !== t5) {
    t6 = <QueryClientProvider client={deps.queryClient}><TRPCProvider locale={i18n.locale}>{t5}</TRPCProvider></QueryClientProvider>;
    $[9] = i18n.locale;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <NotificationsPortal />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6) {
    t8 = <NotificationsContext.Provider value={deps.notifications}>{t6}{t7}</NotificationsContext.Provider>;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== i18n || $[16] !== t8) {
    t9 = <TrackingProvider><I18nContext_Provider i18n={i18n}>{t8}</I18nContext_Provider></TrackingProvider>;
    $[15] = i18n;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
export default GlobalContexts;
const TrackingProvider: React.FC<{
  children: React.ReactNode;
}> = props => {
  const $ = _c(10);
  const visitorId = useCookie(Visitor.Cookie.KEY);
  const session = Auth.useSession();
  let t0;
  if ($[0] !== visitorId.state) {
    t0 = () => {
      const mode = posthogMode();
      if (mode === null) {
        return;
      }
      posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: _POSTHOG_SHARED_CONFIG.HOST,
        ui_host: _POSTHOG_SHARED_CONFIG.UI_HOST,
        person_profiles: "always",
        capture_pageview: false,
        capture_dead_clicks: true,
        capture_pageleave: true,
        capture_performance: true,
        fetch_options: _POSTHOG_SHARED_CONFIG.FETCH_OPTIONS,
        bootstrap: visitorId.state ? {
          distinctID: visitorId.state
        } : undefined,
        ...(mode === "toolbar" && POSTHOG_TOOLBAR_ONLY)
      });
      window.posthog = posthog;
      posthog.register({
        environment: _POSTHOG_SHARED_CONFIG.ENVIRONMENT
      });
      posthog.onFeatureFlags(_temp);
      posthog.reloadFeatureFlags();
      updatePostHogDebugState();
      if (mode === "toolbar") {
        return;
      }
      const TRACKING_PARAMS = ["gclid", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "referrer"];
      const SEARCH_PARAMS = new URLSearchParams(window.location.search);
      const trackingProperties = {};
      for (let i = 0; i < TRACKING_PARAMS.length; i++) {
        const key = TRACKING_PARAMS[i];
        const queryParam = SEARCH_PARAMS.get(key);
        if (queryParam) {
          trackingProperties[key] = queryParam;
          window.sessionStorage.setItem(key, queryParam);
        } else {
          const storageParam = window.sessionStorage.getItem(key);
          if (storageParam) {
            trackingProperties[key] = storageParam;
          }
        }
      }
      if (Object.keys(trackingProperties).length !== 0) {
        posthog.register(trackingProperties);
      }
    };
    $[0] = visitorId.state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== session.user) {
    t2 = () => {
      if (!_POSTHOG_SHARED_CONFIG.ENABLED || !session.user || !posthog.__loaded) {
        return;
      }
      posthog.identify(session.user.email);
    };
    $[3] = session.user;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== session.user || $[6] !== visitorId.state) {
    t3 = [session.user, visitorId.state];
    $[5] = session.user;
    $[6] = visitorId.state;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== props.children) {
    t4 = <PostHogProvider client={posthog}>{props.children}</PostHogProvider>;
    $[8] = props.children;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

/** `tracking` where analytics are on. Elsewhere `toolbar` once someone launches the PostHog
 * toolbar on this deployment, so it has a client to attach to: "Launch" in the PostHog UI puts its
 * state in the URL hash, and the toolbar keeps it in localStorage for the pages after. Otherwise
 * PostHog stays off. */
function posthogMode(): 'tracking' | 'toolbar' | null {
  if (_POSTHOG_SHARED_CONFIG.ENABLED) return 'tracking';
  if (window.location.hash.includes('__posthog=')) return 'toolbar';
  try {
    return window.localStorage.getItem('_postHogToolbarParams') ? 'toolbar' : null;
  } catch {
    return null;
  }
}

/** A deployment with analytics off still runs the toolbar, but sends PostHog nothing. */
const POSTHOG_TOOLBAR_ONLY = {
  opt_out_capturing_by_default: true,
  disable_session_recording: true,
  autocapture: false,
  capture_pageleave: false,
  capture_dead_clicks: false,
  capture_performance: false
} satisfies Partial<PostHogConfig>;
const Lazy_ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools/production').then(res => res.ReactQueryDevtools), {
  ssr: false
});
const REACT_QUERY_DEVTOOLS_ALLOWED_EMAILS = new Set(['kenji@funkey.be', 'louis@funkey.be', 'maxime@funkey.be']);
const Maybe_ReactQueryDevtools: React.FC = _props => {
  const $ = _c(1);
  const session = Auth.useSession();
  const isAllowedUser = session.user?.email ? REACT_QUERY_DEVTOOLS_ALLOWED_EMAILS.has(session.user.email.toLowerCase()) : false;
  if (isAllowedUser !== true) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Lazy_ReactQueryDevtools />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp() {
  return PostHog_ClientFeaturesSDK.syncOverrideCookie(posthog);
}